import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import StatementTableView from './StatementTableView';
import { FinancialStatementService, StatementTable } from '../../services/financialStatement.service';
import { useToast } from '../../context/ToastContext';
import { firstOfYearLocalIso, toLocalIsoDate } from '../../utils/dateUtils';

const EquityStatement: React.FC = () => {
    const toast = useToast();
    const [from, setFrom] = useState(firstOfYearLocalIso());
    const [to, setTo] = useState(toLocalIsoDate());
    const [applied, setApplied] = useState<{ from: string; to: string } | null>(null);
    const [table, setTable] = useState<StatementTable | null>(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setTable(await FinancialStatementService.getEquityStatement(from, to));
            setApplied({ from, to });
        } catch (err) {
            toast.showApiError(err, 'Failed to load the statement of changes in equity');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Downloads use the APPLIED range, never unapplied inputs.
    const download = async (format: 'pdf' | 'xlsx') => {
        if (!applied) return;
        try {
            await FinancialStatementService.downloadEquityStatement(applied.from, applied.to, format);
        } catch (err) {
            toast.showApiError(err, 'Download failed');
        }
    };

    return (
        <Box data-help-page="accounting.equity.page" sx={{ p: 2.5 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Statement of Changes in Equity</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 3 }}>
                <TextField data-help="accounting.equity.from" label="From" type="date" size="small" value={from} onChange={e => setFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 160px', maxWidth: 200 }} />
                <TextField data-help="accounting.equity.to" label="To" type="date" size="small" value={to} onChange={e => setTo(e.target.value)}
                    InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 160px', maxWidth: 200 }} />
                <Button data-help="accounting.equity.apply" variant="contained" onClick={load} disabled={loading}>{loading ? 'Loading...' : 'Apply'}</Button>
                <Button data-help="accounting.equity.pdf" variant="outlined" startIcon={<FileDownloadIcon />} onClick={() => download('pdf')} disabled={!applied}>PDF</Button>
                <Button data-help="accounting.equity.excel" variant="outlined" startIcon={<FileDownloadIcon />} onClick={() => download('xlsx')} disabled={!applied}>Excel</Button>
            </Box>
            {table && <StatementTableView table={table} />}
        </Box>
    );
};

export default EquityStatement;
